import { isEmpty } from "lodash";
import { useCallback, useEffect } from "react";

import { Filter, FilterId, FilterType } from "@/Components/v2/organism/Header/Header";
import { CombinedNode } from "@/pages/scenario-builder/utils/nodes";
import { getMappedGeo, getMappedName } from "@/Utils/mapping-utils";

import { useAllGeographies } from "./api/getAllGeographies";
import { useGeographiesStore } from "./store/useGeographiesStore";
export const useAvailableFilters = ({
  selectedNode,
  startDate,
  endDate,
}: {
  selectedNode?: CombinedNode;
  startDate?: string;
  endDate?: string;
}) => {
  const { setTargetNode, setChannels, setGeographies } = useGeographiesStore();
  const { targetNode, channels, geographies } = useGeographiesStore((state) => state.filters);

  const { data: allGeographies } = useAllGeographies({
    startDate,
    endDate,
    brand: selectedNode?.node.brand ?? "default",
    category: selectedNode?.node.category ?? "default",
    product: selectedNode?.node.product ?? "default",
    config: {
      enabled: !!selectedNode && !!startDate && !!endDate,
    },
  });

  const handleTargetNodeChange = useCallback(
    (targetNode: string | null) => {
      setTargetNode(targetNode);
    },
    [setTargetNode]
  );
  const handleChannelsChange = useCallback(
    (channel: string[]) => setChannels(channel),
    [setChannels]
  );

  const handleGeographiesChange = useCallback(
    (geography: string[]) => setGeographies(geography),
    [setGeographies]
  );

  const filteredGeoOptions = allGeographies
    ?.map((geo) => ({
      label: getMappedGeo(geo) || getMappedName(geo) || geo,
      value: geo,
      selected: false,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const targetNodeFilter = {
    id: FilterId.BusinessUnit,
    label: "Business Unit",
    type: FilterType.BusinessUnit,
    value: targetNode,
    onChange: handleTargetNodeChange,
  };

  const channelsFilter = {
    id: FilterId.Channel,
    label: "Channels",
    type: FilterType.MultiSelect,
    value: channels,
    onChange: handleChannelsChange,
  };

  const geographiesFilter = {
    id: FilterId.Custom,
    label: "Geography",
    type: FilterType.MultiSelect,
    value: geographies,
    onChange: handleGeographiesChange,
    options: filteredGeoOptions,
  };

  useEffect(() => {
    if (!isEmpty(allGeographies)) {
      setGeographies(allGeographies ?? []);
    }
  }, [allGeographies]);

  const filters: Filter[] = [targetNodeFilter, channelsFilter, geographiesFilter];

  return filters;
};
